import {
  DEFAULT_FONT_FAMILY,
  GRAPH_CELL_SIZE,
} from '@control-front-end/common/constants';
import { KEY_FORM_PREFIX } from '@control-front-end/common/constants/forms';
import AppUtils from '../utils';

function prefixKeys(obj, formId) {
  const result = {};
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      result[`${KEY_FORM_PREFIX}${formId}:${key}`] = obj[key];
    }
  }
  return result;
}

function makeBlockActorModel({ id: formId, parentId, data, color }) {
  const { shape, size, textFrame } = data.view;
  const areaPicture = AppUtils.createAreaPictureByShape(shape, size, color);
  return {
    formId: parentId,
    form: { id: parentId },
    formData: prefixKeys(data, formId),
    areaPicture,
    color,
    isFlowchart: true,
    layerSettings: {
      width: size.w,
      height: size.h,
      textFrame,
      blockId: data.blockId,
    },
  };
}

/**
 * Function to extract form ID from the first top-level key.
 * @param {Object} model - The object containing form data.
 * @returns {string|null} - The extracted form ID or null if not found.
 */
function getFlowchartFormId(model) {
  if (!model || typeof model !== 'object') return null;

  const flowchartBlockKey = Object.keys(model).find(
    (key) => key.startsWith(KEY_FORM_PREFIX) && key.includes(':blockId')
  );

  if (!flowchartBlockKey) return null;

  // Extract the numeric ID from the key
  const matches = flowchartBlockKey.match(
    new RegExp(
      `${KEY_FORM_PREFIX.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}(\\d+):`
    )
  );
  return matches ? matches[1] : null;
}

function createAreaPictureByShape(shape, size, color = '#ffffff') {
  const fillColor = color;
  const strokeColor = AppUtils.getDarkenColor(color, 0.2);
  const strokeWidth = 2;

  let svgContent = '';

  if (shape.type === 'path') {
    svgContent += `<path
      d="${shape.d}"
      fill="${fillColor}"
      stroke="${strokeColor}"
      stroke-width="${strokeWidth}"
    />`;
  } else if (shape.type === 'primitive') {
    shape.primitive.forEach((primitive) => {
      switch (primitive.kind) {
        case 'rect':
          svgContent += `<rect
            x="${primitive.x}"
            y="${primitive.y}"
            width="${primitive.w}"
            height="${primitive.h}"
            fill="${fillColor}"
            stroke="${strokeColor}"
            stroke-width="${strokeWidth}"
          />`;
          break;

        case 'roundedRect':
          svgContent += `<rect
            x="${primitive.x}"
            y="${primitive.y}"
            width="${primitive.w}"
            height="${primitive.h}"
            rx="${primitive.rx}"
            ry="${primitive.ry}"
            fill="${fillColor}"
            stroke="${strokeColor}"
            stroke-width="${strokeWidth}"
          />`;
          break;

        case 'line':
          svgContent += `<line
            x1="${primitive.x1}"
            y1="${primitive.y1}"
            x2="${primitive.x2}"
            y2="${primitive.y2}"
            stroke="${strokeColor}"
            stroke-width="${strokeWidth}"
          />`;
          break;

        case 'path':
          svgContent += `<path
            d="${primitive.d}"
            fill="${fillColor}"
            stroke="${strokeColor}"
            stroke-width="${strokeWidth}"
          />`;
          break;

        default:
          // eslint-disable-next-line no-console
          console.warn(`Unknown primitive kind: ${primitive.kind}`);
          break;
      }
    });
  }

  const svg = `<svg
    xmlns="http://www.w3.org/2000/svg"
    width="${size.w}"
    height="${size.h}"
    viewBox="0 0 ${size.w} ${size.h}"
  >${svgContent}</svg>`;

  const svgBase64 = `data:image/svg+xml;base64,${btoa(
    unescape(encodeURIComponent(svg))
  )}`;

  return {
    img: svgBase64,
    width: size.w,
    height: size.h,
    type: 'flowchart',
  };
}

function scaleTextFrame({ textFrame, baseSize, size }) {
  const scaleX = size.width / baseSize.w;
  const scaleY = size.height / baseSize.h;
  return {
    x: textFrame.x * scaleX,
    y: textFrame.y * scaleY,
    w: textFrame.w * scaleX,
    h: textFrame.h * scaleY,
  };
}

/**
 * Renders text inside a flowchart shape with proper wrapping and constraints
 *
 * @param {Object} params - Parameters for rendering text
 * @param {CanvasRenderingContext2D} params.ctx - Canvas rendering context
 * @param {string} params.text - Text to render
 * @param {Object} params.textFrame - Text frame with x, y of a left top corner and w, h
 * @param {number} params.centerX - X center position of the node
 * @param {number} params.centerY - Y center position of the node
 * @param {number} params.fontSize - Font size to use (default: 14px)
 * @param {string} params.fontFamily - Font family to use
 * @param {number} params.opacity - Opacity for the text (0-1)
 */
function renderFlowchartText({
  ctx,
  text,
  textFrame,
  nodeBoundingBox,
  fontSize = 14,
  fontFamily = DEFAULT_FONT_FAMILY,
  opacity = 1,
}) {
  const centerX = nodeBoundingBox.x1 + textFrame.x + textFrame.w / 2;
  const centerY = nodeBoundingBox.y1 + textFrame.y + textFrame.h / 2;
  const minWidth = GRAPH_CELL_SIZE / 2;
  if (
    !ctx ||
    !text ||
    !textFrame ||
    !textFrame.w ||
    textFrame.w < minWidth ||
    !textFrame.h
  )
    return;

  // Use getWrappedText with fixed font size but constrained width
  const wrappedLines = AppUtils.getWrappedText({
    text,
    maxWidth: textFrame.w,
    fontSize,
    fontFamily,
    breakAll: false,
  });

  // Fixed line height based on font size
  const lineHeight = fontSize * 1.3; // Slightly more spacing for readability

  // Calculate the maximum number of lines that can fit in the available height
  const maxLines = Math.floor(textFrame.h / lineHeight);

  // Use all available lines up to the maximum that can fit
  // This allows text to expand when more space is available
  let displayLines = wrappedLines;
  if (wrappedLines.length > maxLines) {
    displayLines = wrappedLines.slice(0, maxLines);

    // Add ellipsis to the last line if truncated
    if (displayLines.length > 0) {
      const lastLine = displayLines[displayLines.length - 1];
      const ellipsisWidth = AppUtils.measureTextWidth({
        text: '...',
        fontSize,
        fontFamily,
      });

      if (
        AppUtils.measureTextWidth({
          text: lastLine,
          fontSize,
          fontFamily,
        }) +
          ellipsisWidth >
        textFrame.w
      ) {
        // Need to truncate the last line to make room for ellipsis
        let truncatedLastLine = lastLine;
        while (
          truncatedLastLine.length > 0 &&
          AppUtils.measureTextWidth({
            text: `${truncatedLastLine}...`,
            fontSize,
            fontFamily,
          }) > textFrame.w
        ) {
          truncatedLastLine = truncatedLastLine.slice(0, -1);
        }
        displayLines[displayLines.length - 1] = `${truncatedLastLine}...`;
      } else {
        displayLines[displayLines.length - 1] = `${lastLine}...`;
      }
    }
  }

  // Save current context state
  ctx.save();
  ctx.font = `${fontSize}px ${fontFamily}`;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillStyle = `rgba(0, 0, 0, ${opacity})`;

  // Calculate total text height to center vertically
  const totalTextHeight = displayLines.length * lineHeight;
  const startY = centerY - totalTextHeight / 2 + lineHeight / 2;

  // Draw each line of text
  displayLines.forEach((line, index) => {
    const lineY = startY + index * lineHeight;
    ctx.fillText(line, centerX, lineY, textFrame.w);
  });

  ctx.restore();
}

export default {
  createAreaPictureByShape,
  scaleTextFrame,
  renderFlowchartText,
  makeBlockActorModel,
  getFlowchartFormId,
};
